import { Injectable } from '@angular/core';
import { DialogCreator } from "sharedFeatures/window";
import { Button } from '../../dialog-component/button.model';
import { DialogPopupComponent } from '../../dialog-component/dialog-popup.component';
import { LoadingSpinnerService } from 'sharedGuiSmall/sharedServices/loadingSpinner.service';
import { TranslateService } from '@ngx-translate/core'

@Injectable()
export class DialogCreationService {

    private dialogCreator!: DialogCreator;

    constructor(private loadingSpinnerService: LoadingSpinnerService, private translate: TranslateService) { }

    public setDialogCreator(creator: DialogCreator) {
        this.dialogCreator = creator;
    }

    public getDialogCreator(): DialogCreator {
        return this.dialogCreator;
    }

    createBasicDialog(planName: string, deleteHandler?: Function) {

        const popup: DialogPopupComponent = <DialogPopupComponent>this.dialogCreator.createDialog(DialogPopupComponent);

        // Use TranslateService to fetch translations
        this.translate.get([
            'CONFIRM_DELETE',
            'DELETE_PLAN_CONFIRMATION',
            'DELETE',
            'CANCEL'
        ], { planName }) // Pass planName as a parameter for string interpolation
            .subscribe(translations => {
                popup.setTitle(translations['CONFIRM_DELETE']);
                popup.setBody(translations['DELETE_PLAN_CONFIRMATION']);
                popup.setTestId("DeletePlanItemConfirm");

                let deleteButton: Button = new Button(
                    translations['DELETE'],
                    true,
                    145,
                    deleteHandler ? deleteHandler : () => { },
                    "DeletePlanItemConfirmConfirm"
                );

                let cancelButton: Button = new Button(
                    translations['CANCEL'],
                    false,
                    145,
                    () => { popup.close(); console.log('close'); },
                    "DeletePlanItemConfirmCancel"
                );

                let array = [cancelButton, deleteButton];
                popup.setButtonArray(array);
                popup.setHeight(235);
                popup.setWidth(430);
                popup.setButtonsTopMargin(30);
                popup.setDeleteButton(false);
                popup.setDisableButtonsAfterAction(true);
            });

        return popup;

    }

    createContinueEnableDriversDialog(tableName: string, continueHandler?: Function) {

        const popup: DialogPopupComponent = <DialogPopupComponent>this.dialogCreator.createDialog(DialogPopupComponent);

        // Use TranslateService to fetch translations
        this.translate.get([
            'ARE_YOU_SURE_YOU_WANT_TO_CONTINUE',
            'ENABLING_DRIVERS_WILL_OVERWRITE_NUMBERS_IN_TABLE',
            'CONTINUE',
            'CANCEL'
        ], { tableName }) // Pass tableName as a parameter for string interpolation
            .subscribe(translations => {
                popup.setTitle(translations['ARE_YOU_SURE_YOU_WANT_TO_CONTINUE']);
                popup.setBody(translations['ENABLING_DRIVERS_WILL_OVERWRITE_NUMBERS_IN_TABLE']);
                popup.setTestId("ContinueEnableDrivers");

                let continueButton: Button = new Button(
                    translations['CONTINUE'],
                    true,
                    145,
                    continueHandler ? continueHandler : () => { },
                    "ContinueEnableDriversContinue"
                );

                let cancelButton: Button = new Button(
                    translations['CANCEL'],
                    false,
                    145,
                    () => { popup.close(); console.log('close'); },
                    "ContinueEnableDriversCancel"
                );

                let array = [cancelButton, continueButton];
                popup.setButtonArray(array);
                popup.setHeight(235);
                popup.setWidth(430);
                popup.setButtonsTopMargin(30);
                popup.setDeleteButton(false);
                popup.setDisableButtonsAfterAction(true);
            });

        return popup;

    }
    
    createDeleteCompanyDialog(organisationName: string ,deleteHandler?: Function){
        const popup: DialogPopupComponent = <DialogPopupComponent>this.dialogCreator.createDialog(DialogPopupComponent);

        // Fetch translations dynamically
        this.translate.get([
            'CONFIRM_DELETE',
            'DELETE_COMPANY_CONFIRMATION',
            'DELETE',
            'CANCEL'
        ], { organisationName }) // Pass organisationName for dynamic interpolation
            .subscribe(translations => {
                popup.setTitle(translations['CONFIRM_DELETE']);
                popup.setBody(translations['DELETE_COMPANY_CONFIRMATION']);
                popup.setTestId('DeleteCompanyConfirm');

                let deleteButton: Button = new Button(
                    translations['DELETE'],
                    true,
                    145,
                    deleteHandler ? deleteHandler : () => { },
                    'DeleteCompanyConfirmConfirm'
                );

                let cancelButton: Button = new Button(
                    translations['CANCEL'],
                    false,
                    145,
                    () => { popup.close(); console.log('close'); },
                    'DeleteCompanyConfirmCancel'
                );

                let array = [cancelButton, deleteButton];
                popup.setButtonArray(array);
                popup.setWidth(425);
                popup.setHeight(235);
                popup.setButtonsTopMargin(30);
                popup.setDeleteButton(false);
                popup.setDisableButtonsAfterAction(true);
            });

        return popup;
    }

    createXeroDisconnectDialog(companyName: string, plansCount: number, deleteHandler?: Function) {
        const popup: DialogPopupComponent = <DialogPopupComponent>this.dialogCreator.createDialog(DialogPopupComponent);

        popup.setTitle("Confirm company disconnection");
        popup.setTestId("DisconnectXeroPlanItemConfirm");
        let deleteButton: Button = new Button("Disconnect", true, 145, deleteHandler ? deleteHandler : function () { }, "DisconnectXeroPlanItemConfirmConfirm");
        let cancelButton: Button = new Button("Don't disconnect", false, 145, () => { popup.close(); console.log('close'); }, "DisconnectXeroPlanItemConfirmCancel");
        let array = [cancelButton, deleteButton];
        popup.setButtonArray(array);
        let height = plansCount > 0 ? 375 : 290;
        popup.setHeight(height);
        popup.setButtonsTopMargin(30);
        popup.setDeleteButton(false);
        popup.setDisableButtonsAfterAction(true);
        popup.setXeroDisconnect(companyName, plansCount);
        popup.showClose = true;

        return popup;
    }

    createXeroRefreshErrorDialog(signHandler?: Function, cancelHandler?: Function, bodyText?: string) {
        const popup: DialogPopupComponent = <DialogPopupComponent>this.dialogCreator.createDialog(DialogPopupComponent);

        this.translate.get([
            'XERO_AUTH_REQUIRED',
            'XERO_AUTH_BODY',
            'RECONNECT',
            'CANCEL'
        ], { bodyText })
            .subscribe(translations => {
                popup.setTitle(translations['XERO_AUTH_REQUIRED']);
                popup.setBody(bodyText ? bodyText : translations['XERO_AUTH_BODY']);
                popup.setTestId('XeroRefreshAuthFailed');

                let signButton: Button = new Button(
                    translations['RECONNECT'],
                    true,
                    145,
                    signHandler ? signHandler : () => console.log('sign in'),
                    'XeroRefreshAuthFailedSignIn'
                );

                let cancelButton: Button = new Button(
                    translations['CANCEL'],
                    false,
                    145,
                    cancelHandler ? cancelHandler : () => { popup.close(); console.log('close'); },
                    'XeroRefreshAuthFailedCancel'
                );

                let array = [cancelButton, signButton];
                popup.setButtonArray(array);
                popup.setHeight(290);
                popup.setButtonsTopMargin(30);
                popup.setDeleteButton(false);
                popup.setDisableButtonsAfterAction(true);
                popup.showClose = true;
            });

        return popup;
    }

    createRemoveSharedDialog(planName: string, removeHandler?: Function) {
        const popup: DialogPopupComponent = <DialogPopupComponent>this.dialogCreator.createDialog(DialogPopupComponent);

        this.translate.get([
            'CONFIRM_REMOVE',
            'REMOVE_PLAN_CONFIRMATION',
            'REMOVE',
            'CANCEL'
        ], { planName })
            .subscribe(translations => {
                popup.setTitle(translations['CONFIRM_REMOVE']);
                popup.setBody(translations['REMOVE_PLAN_CONFIRMATION']);
                popup.setTestId('RemoveSharedPlanItemConfirm');

                let deleteButton: Button = new Button(
                    translations['REMOVE'],
                    true,
                    145,
                    removeHandler ? removeHandler : () => { },
                    'RemoveSharedPlanItemConfirmDelete'
                );

                let cancelButton: Button = new Button(
                    translations['CANCEL'],
                    false,
                    145,
                    () => { popup.close(); },
                    'RemoveSharedPlanItemConfirmCancel'
                );

                let array = [cancelButton, deleteButton];
                popup.setButtonArray(array);
                popup.setSize(400, 295);
                popup.setButtonsTopMargin(30);
                popup.setDeleteButton(false);
                popup.setDisableButtonsAfterAction(true);
            });

        return popup;
    }


    createErrorWarning(
        title: string,
        body: string,
        okHandler?: Function,
        height?: number,
        buttonTitle?: string,
        width?: number,
        testId: string = '',
        bodyStyle: string = ''
    ) {
        const popup: DialogPopupComponent = <DialogPopupComponent>this.dialogCreator.createDialog(DialogPopupComponent);

        this.translate.get([
            title,
            body,
            buttonTitle || 'OK'
        ])
            .subscribe(translations => {
                popup.setTitle(translations[title]);
                popup.setBody(translations[body]);

                if (bodyStyle)
                    popup.setBodyStyle(bodyStyle);

                popup.setTestId(testId);

                let warningButton: Button = new Button(
                    translations[buttonTitle || 'OK'],
                    true,
                    240,
                    okHandler,
                    testId + 'Confirm'
                );

                let array = [warningButton];
                popup.setButtonArray(array);
                popup.setHeight(height || 260);
                popup.setWidth(width || 600);
                popup.setButtonsTopMargin(45);
            });

        return popup;
    }

    createUnknownExceptionDialog(error: string): DialogPopupComponent {
        this.loadingSpinnerService.setLoading(false);
        const popup: DialogPopupComponent = <DialogPopupComponent>this.dialogCreator.createDialog(DialogPopupComponent);

        this.translate.get(['UNKNOWN_ERROR', 'CANCEL'], { error })
            .subscribe(translations => {
                popup.setTitle(translations['UNKNOWN_ERROR']);
                popup.setBody(error);

                let warningButton: Button = new Button(
                    translations['CANCEL'],
                    true,
                    240,
                    () => { popup.close(); },
                    'UnknownErrorCancel'
                );

                let array = [warningButton];
                popup.setButtonArray(array);
                popup.setHeight(240);
                popup.setButtonsTopMargin(40);
                popup.setDeleteButton(false);
            });

        return popup;
    }

}